import { useLoaderData, useNavigate } from "react-router-dom";
import { AviTracerApi } from "../../../avitracerApi";
import Flight, { FlightFplFile, flightIsWaitingForRiskAssessment, isIfrFlightRules } from "../../../models/flight";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { cancelFpl, delayFpl, fileFpl, upsertFlight } from "../../../store/flightSlice";
import BaseFplPage, { BaseIfrSelectResult } from "../BaseFplPage";
import { IfrPlanningOverrides, IfrRouteResult } from "../../../avitracerApi";
import { selectedOrganization } from "../../../store/organizationsSlice";

function FlightFplPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const loadedFlight = useLoaderData() as Flight;

  const selectedOrg = useAppSelector(selectedOrganization);
  
  const getPdfDoc = async () => {
    const pdfData = await AviTracerApi.getPdfDoc(loadedFlight.id!, "icaoFpl");
    return pdfData;
  };

  //Errors are NOT caught here — they propagate to BaseFplPage, which shows them inline and keeps the
  //user on the page (no js alerts in this project).
  const fileFplUsingAviTracer = async () => {
    await dispatch(fileFpl(loadedFlight.id!)).unwrap();
  };

  const refileFpl = async () => {
    await AviTracerApi.refileFpl(loadedFlight.id!);
  };

  const chgFpl = async (f15: string, f18: string) => {
    await AviTracerApi.chgFpl(loadedFlight.id!, f15, f18);
  }

  const cancelFiledFpl = async () => {
    await dispatch(cancelFpl(loadedFlight.id!)).unwrap();
  };

  const delayFiledFpl = async (newTime: Date) => {
    await dispatch(delayFpl({ flightId: loadedFlight.id!, newTime: newTime })).unwrap();
  };

  const findIfrRoutes = async (optimizes: string[], levels?: number[], overrides?: IfrPlanningOverrides): Promise<IfrRouteResult[]> => {
    const resp = await AviTracerApi.findIfrRoutes(loadedFlight.id!, optimizes, levels, overrides);
    return resp.results;
  };

  const checkManualRoute = async (field15: string, overrides?: IfrPlanningOverrides): Promise<IfrRouteResult> => {
    const resp = await AviTracerApi.checkIfrRoute(loadedFlight.id!, field15, overrides);
    return resp.result;
  };

  const icaoOrUndef = (name?: string) => (name && /^[A-Z]{4}$/.test(name.toUpperCase()) ? name.toUpperCase() : undefined);
  const ifrPlanningDefaults = {
    departure: loadedFlight.route
      ? icaoOrUndef(loadedFlight.route.departure?.name)
      : loadedFlight.customRoute?.departure?.isIcao
      ? loadedFlight.customRoute.departure.icaoCode
      : undefined,
    destination: loadedFlight.route
      ? icaoOrUndef(loadedFlight.route.destination?.name)
      : loadedFlight.customRoute?.destination?.isIcao
      ? loadedFlight.customRoute.destination.icaoCode
      : undefined,
    alternate1: loadedFlight.altn1,
    alternate2: loadedFlight.altn2,
  };

  const selectIfrRoute = async (field15: string, overrides?: IfrPlanningOverrides): Promise<BaseIfrSelectResult> => {
    const resp = await AviTracerApi.selectIfrRoute(loadedFlight.id!, field15, overrides);
    dispatch(upsertFlight(resp.flight));
    return { fplFile: resp.flight.fplFile, ifpsRoute: resp.flight.ifpsRoute };
  };

  const validateFpl = async () => {
    const resp = await AviTracerApi.validateFpl(loadedFlight.id!);
    //The backend persisted this result on the flight — mirror it into Redux so the flights list badge
    //reflects it too (otherwise the list keeps showing the pre-validation "Ready to validate" state).
    dispatch(
      upsertFlight({
        ...loadedFlight,
        ifrValidation: {
          result: resp.validation.result,
          validation: resp.validation.validation,
          errors: resp.validation.errors,
          fplCode: resp.fplCode,
          at: new Date().toISOString(),
        },
      })
    );
    return resp;
  };

  const applyProposedRoute = async (route: string) => {
    const resp = await AviTracerApi.applyProposedRoute(loadedFlight.id!, route);
    dispatch(upsertFlight(resp));
    return resp;
  };

  const goBack = () => {
    navigate("/flights");
  };

  const goToSchedule = () => {
    navigate(`/flights/${loadedFlight.id}/schedule`);
  }

  const getFplFileAfterRiskAssessmentCheck = () => {
    if (selectedOrg && flightIsWaitingForRiskAssessment(loadedFlight, selectedOrg)){
      return {
        status: "error",
        error: "Risk assessment not ready.",
      } as FlightFplFile
    }else if (selectedOrg && loadedFlight.customDocs?.riskAssessment?.acceptanceStatus === false){
      return {
        status: "error",
        error: "Risk assessment rejected.",
      } as FlightFplFile
    }
    return loadedFlight.fplFile
  }
  
  return (
    <BaseFplPage
      data={{
        fplFile: getFplFileAfterRiskAssessmentCheck(),
        flightPlanPicDetails: loadedFlight.pic.flightPlanPicDetails,
        departureDateTime: new Date(loadedFlight.departureDateTime),
        flightId: loadedFlight.flightId,
        isIfr: isIfrFlightRules(loadedFlight.flightRules),
        ifpsRoute: loadedFlight.ifpsRoute,
        altn1: loadedFlight.altn1,
        //Straight off the flight (copied from the aircraft when it was picked). No aircraft fallback —
        //if these are genuinely missing the page should say so, not quietly plan on unseen values.
        ifrFuelPerHour: loadedFlight.ifrFuelPerHour,
        ifrMaxMass: loadedFlight.ifrMaxMass,
        ifrValidation: loadedFlight.ifrValidation,
      }}
      getPdfDoc={getPdfDoc}
      fileFplUsingAviTracerAction={fileFplUsingAviTracer}
      refileFplAction={refileFpl}
      chgFplAction={chgFpl}
      cancelFiledFplAction={cancelFiledFpl}
      delayFiledFplAction={delayFiledFpl}
      findIfrRoutesAction={findIfrRoutes}
      checkManualRouteAction={checkManualRoute}
      ifrPlanningDefaults={ifrPlanningDefaults}
      selectIfrRouteAction={selectIfrRoute}
      validateFplAction={validateFpl}
      applyProposedRouteAction={applyProposedRoute}
      goBack={goBack}
      goToSchedule={goToSchedule}
    />
  );
}

export default FlightFplPage